import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWineGlassAlt, 
  faSeedling, 
  faWineBottle, 
  faCalendarAlt,
  faMapMarkedAlt,
  faMountain,
  faWineGlassEmpty,
  faLeaf
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';

// Import images from existing assets
import vineyardBanner from '../../../assets/banner/vineyard.webp'; // Banner image
import vineyardPhoto from '../../../assets/vineyard.webp'; // The vineyard image

const VineyardTourContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const BookNowButton = styled.a`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const TourDates = styled.div`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin: 2rem 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  text-align: center;
`;

const DateTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: white;
`;

const DateItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.3rem;
  font-weight: 500;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    margin-right: 1rem;
    color: white;
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TourSchedule = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  margin: 2rem 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const ScheduleItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    margin-right: 1rem;
    color: ${props => props.theme.colors.accent};
  }
`;

const PreviewSection = styled.div`
  margin: 5rem 0;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }
`;

const PreviewContent = styled.div`
  padding: 2rem;
  text-align: center;
`;

const PreviewTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const PreviewDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  color: white;
  font-size: 32px;
`;

const ImageSection = styled.div`
  margin: 4rem 0;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const VineyardImageWrapper = styled.div`
  width: 100%;
  height: 500px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 350px;
  }
`;

const VineyardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.03);
  }
`;

const ImageCaption = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  color: white;
  padding: 2rem;
  text-align: center;
`;

const CaptionText = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
`;

const VineyardTour = () => {
  const { ref: previewRef, inView: previewInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const { ref: imageRef, inView: imageInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  return (
    <VineyardTourContainer>
      <ParallaxBanner 
        title="Belvoir Castle Vineyard Tours"
        subtitle="Experience Our Heritage Wines"
        image={vineyardBanner}
      />
      
      <ContentContainer>
        <SectionTitle>Belvoir Castle Vineyard Experience</SectionTitle>
        <IntroText>
          Join us for an unforgettable journey through our historic vineyards. Experience the art of winemaking,
          guided by our expert Mark Bygott, and indulge in a delightful wine tasting session followed by a
          sumptuous supper.
        </IntroText>

        <TourDates>
          <DateTitle>Upcoming Tour Dates</DateTitle>
          <DateItem>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Coming Soon...
          </DateItem>
        </TourDates>

        <TourSchedule>
          <ScheduleItem>
            <FontAwesomeIcon icon={faMapMarkedAlt} />
            Meet at Engine Yard
          </ScheduleItem>
          <ScheduleItem>
            <FontAwesomeIcon icon={faMountain} />
            Gun Bus to Vineyard
          </ScheduleItem>
          <ScheduleItem>
            <FontAwesomeIcon icon={faSeedling} />
            Tour with Mark Bygott
          </ScheduleItem>
          <ScheduleItem>
            <FontAwesomeIcon icon={faWineGlassAlt} />
            Wine Tasting
          </ScheduleItem>
          <ScheduleItem>
            <FontAwesomeIcon icon={faWineBottle} />
            Supper
          </ScheduleItem>
          <ScheduleItem>
            <FontAwesomeIcon icon={faMountain} />
            Gun Bus Back to Engine Yard
          </ScheduleItem>
        </TourSchedule>

        <div style={{ textAlign: 'center' }}>
          <BookNowButton 
            href=""
            target="_blank"
            rel="noopener noreferrer"
          >
            More Dates Coming Soon
          </BookNowButton>
        </div>

        <PreviewSection ref={previewRef}>
          <PreviewGrid>
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faWineGlassAlt} />
                </IconWrapper>
                <PreviewTitle>Wine Tasting</PreviewTitle>
                <PreviewDescription>
                  Sample our finest wines while learning about their unique characteristics
                  and the winemaking process.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
            
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faSeedling} />
                </IconWrapper>
                <PreviewTitle>Vineyard Tour</PreviewTitle>
                <PreviewDescription>
                  Explore our historic vineyards with expert guide Mark Bygott and learn
                  about our winemaking heritage.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
            
            <PreviewCard>
              <PreviewContent>
                <IconWrapper>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </IconWrapper>
                <PreviewTitle>Complete Experience</PreviewTitle>
                <PreviewDescription>
                  Enjoy a full day of wine education, tasting, and dining in the beautiful
                  surroundings of Belvoir Castle.
                </PreviewDescription>
              </PreviewContent>
            </PreviewCard>
          </PreviewGrid>
        </PreviewSection>

        <ImageSection ref={imageRef}>
          <VineyardImageWrapper>
            <VineyardImg src={vineyardPhoto} alt="Belvoir Castle Vineyard" />
            <ImageCaption>
              <CaptionText>Discover the Art of Winemaking at Belvoir Castle</CaptionText>
            </ImageCaption>
          </VineyardImageWrapper>
        </ImageSection>
      </ContentContainer>
      <Footer />
    </VineyardTourContainer>
  );
};

export default VineyardTour; 